// Metric entry, independent of which workspace is active (#307).
//
// One row per metric per period, upserted on `(metric_id, period)`. Two people
// entering different periods concurrently no longer touch the same object, so
// the merge function that #238 needed is gone: the unique constraint is what
// makes the write safe. The row the database returns replaces the optimistic
// one, so local state is reconciled against the database rather than against
// the client's own guess at it.
import { applyWrite } from '@/shared/lib/store-write';
import { entryTipsOffTrack, withEntry } from '@/modules/scorecards/entry-rules';
import type { Metric, MetricEntry } from '@/modules/scorecards/types';
import type { MetricThresholds } from '@/modules/workspaces/workspace-settings';

export type { MetricThresholds } from '@/modules/workspaces/workspace-settings';

import type { MutationContext } from '@/modules/activity/mutation-context';

export { entryTipsOffTrack, withEntry };

/**
 * As much of a metric as a write needs to know: enough to describe the change,
 * decide whether it tips off track, and address the notification.
 *
 * Deliberately not the whole `Metric` — a cross-workspace caller holds rows it
 * fetched for display, not hydrated domain objects.
 */
export type MetricSnapshot = Pick<Metric, 'entries' | 'title' | 'ownerId' | 'targets' | 'origin'>;

export function setMetricEntry(
  ctx: MutationContext,
  metricId: string,
  period: string,
  value: number | null,
  before: MetricSnapshot | undefined,
  hooks: {
    /** Optimistic display, replaced by `reconcile` once the write lands. */
    apply(entries: MetricEntry[]): void;
    /** The array the database actually holds. */
    reconcile(entries: MetricEntry[]): void;
    thresholds: MetricThresholds;
  },
): void {
  if (before) hooks.apply(withEntry(before.entries, period, value));

  applyWrite(
    ctx.api(`/metrics/${metricId}/entries`, { period, value }),
    {
      noun: 'this entry',
      context: 'setMetricEntry',
      refetch: ctx.refetch,
      onSuccess: (rows) => {
        // The upsert returns the row it wrote, including the authorship the
        // database stamped — `entered_by` is never accepted from here. It stays
        // an array rather than `.single()`: `applyWrite` tells a refusal from a
        // success by counting rows, and an object has none to count.
        const row = (rows as any[])[0] ?? {};
        const written: MetricEntry = {
          period: row.period ?? period,
          value: row.value ?? null,
          enteredBy: row.entered_by ?? null,
          enteredAt: row.entered_at ?? null,
        };
        const entries = withEntry(before?.entries ?? [], period, value)
          .map((e) => e.period === period ? written : e);
        hooks.reconcile(entries);
      },
    },
  );
}
